import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  AtSign,
  Bell,
  BellOff,
  CheckCheck,
  Copy,
  Ellipsis,
  Folder,
  FolderOpen,
  GripVertical,
  Grid3x3,
  Hash,
  Info,
  Link2,
  Lock,
  LogOut,
  Mail,
  MailOpen,
  MessageCircle,
  Pencil,
  Plus,
  SlidersHorizontal,
  Star,
  StarOff,
  Trash2,
  X,
} from '@/components/ui/icons';
import type { Channel } from '@shared/types';
import { useDb, useDbShallow, useMe, type Tables } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { setChatListPrefs, toggleExpanded, useUi, type ChatListPrefs } from '@/lib/ui';
import { chatPrefsOf, chatSectionsOf, deleteChatSection, markChannelRead, moveChatSection, moveToSection, sectionByChannel, setChatPrefs } from '@/lib/actions';
import { leaveChannel, markChannelUnread, toggleStar, useStarredIds } from '@/pages/chat/utils';
import { MuteMenu } from '@/pages/chat/NotifyControls';
import { Avatar } from '@/components/ui/Avatar';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu, toast } from '@/components/ui/overlays';
import { SidebarItem, SidebarSection } from './SidebarItem';
import { openDialog } from './dialogs';
import { StatusBadge } from './SetStatus';
import { useIsGuest } from '@/lib/roles';

export function channelTitle(db: Tables, ch: Channel, me: string) {
  if (ch.kind === 'channel') return ch.name;
  const others = ch.memberIds.filter((u) => u !== me);
  if (others.length === 0) return `${db.users[me]?.name ?? 'Me'}`;
  return others.map((u) => db.users[u]?.name ?? 'Unknown').join(', ');
}

export function useUnreadByChannel() {
  const me = useMe();
  return useDb((db) => {
    const lastRead: Record<string, number> = {};
    for (const r of Object.values(db.channelReads)) if (r.userId === me) lastRead[r.channelId] = r.lastReadAt;
    const counts: Record<string, number> = {};
    for (const m of Object.values(db.messages)) {
      if (m.authorId === me || m.parentId) continue;
      if (m.createdAt > (lastRead[m.channelId] ?? 0)) counts[m.channelId] = (counts[m.channelId] ?? 0) + 1;
    }
    return JSON.stringify(counts);
  });
}

/** Unread count for a channel or DM row: a red circle (a pill past one digit). */
export const unreadBadge = (n: number | undefined) => (n ? <span className="sb-count-badge">{n > 99 ? '99+' : n}</span> : undefined);

/**
 * "Move to section" for a conversation: my sections, a new one, or back out of the current one.
 * Takes the workspace from the conversation: right-click menus render outside the page's route.
 */
export function MoveToSectionMenu({ channelId, close }: { channelId: string; close: () => void }) {
  const ws = useDb((d) => d.channels[channelId]?.workspaceId ?? '');
  const me = useMe();
  const sections = useDbShallow((d) => chatSectionsOf(d, me, ws));
  const current = useDb((d) => sectionByChannel(d, me)[channelId] ?? null);
  return (
    <Menu style={{ width: 230 }}>
      <MenuLabel>Move to section</MenuLabel>
      {sections.map((s) => (
        <MenuItem key={s.id} icon={<Folder size={15} />} label={s.name} checked={s.id === current} onClick={() => (close(), moveToSection(channelId, s.id), toast(`Moved to ${s.name}`))} />
      ))}
      <MenuItem icon={<Plus size={15} />} label="New section…" onClick={() => (close(), openDialog({ kind: 'chat-section', workspaceId: ws, channelId }))} />
      {current && (
        <>
          <MenuSeparator />
          <MenuItem icon={<X size={15} />} label="Remove from section" onClick={() => (close(), moveToSection(channelId, null))} />
        </>
      )}
    </Menu>
  );
}

/**
 * The ⋯ (and right-click) menu of a channel or DM in the sidebar: details, copy, star, what notifies
 * you, more options (section, read state) and leaving the channel.
 */
export function ConversationMenu({ channel, close }: { channel: Channel; close: () => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const ws = channel.workspaceId;
  const isDm = channel.kind === 'dm';
  const stars = useStarredIds();
  const starred = stars.includes(channel.id);
  const prefs = useDbShallow((d) => chatPrefsOf(d, me, channel));
  const title = useDb((d) => channelTitle(d, channel, me));
  const link = `${window.location.origin}/${ws}/chat/${channel.id}`;
  const copy = (text: string, what: string) => {
    void navigator.clipboard.writeText(text);
    toast(`${what} copied`);
    close();
  };
  const notify = (n: 'all' | 'mentions') => {
    setChatPrefs(channel.id, { notify: n, mutedUntil: null });
    close();
  };
  return (
    <Menu style={{ width: 250 }}>
      <MenuItem
        icon={<Info size={15} />}
        label={isDm ? 'Conversation details' : 'Channel details'}
        onClick={() => (close(), navigate(`/${ws}/chat/${channel.id}?panel=${isDm ? 'profile' : 'settings'}`))}
      />
      <MenuItem
        icon={<Copy size={15} />}
        label="Copy"
        submenu={(c2) => (
          <Menu style={{ width: 200 }}>
            <MenuItem icon={<Link2 size={15} />} label="Copy link" onClick={() => (c2(), copy(link, 'Link'))} />
            <MenuItem icon={isDm ? <MessageCircle size={15} /> : <Hash size={15} />} label="Copy name" onClick={() => (c2(), copy(isDm ? title : `#${channel.name}`, 'Name'))} />
          </Menu>
        )}
      />
      <MenuItem icon={starred ? <StarOff size={15} /> : <Star size={15} />} label={starred ? `Unstar ${isDm ? 'conversation' : 'channel'}` : `Star ${isDm ? 'conversation' : 'channel'}`} onClick={() => (close(), toggleStar(channel.id))} />
      <MenuSeparator />
      <MenuLabel>Notify you about…</MenuLabel>
      <MenuItem icon={<Bell size={15} />} label="All new posts" checked={prefs.notify === 'all' && !prefs.mutedUntil} onClick={() => notify('all')} />
      <MenuItem icon={<AtSign size={15} />} label="Just mentions" checked={prefs.notify === 'mentions' && !prefs.mutedUntil} onClick={() => notify('mentions')} />
      <MenuItem icon={<BellOff size={15} />} label={prefs.mutedUntil ? 'Muted' : 'Mute'} checked={!!prefs.mutedUntil} submenu={(c2) => <MuteMenu channel={channel} close={() => (c2(), close())} />} />
      <MenuItem
        icon={<SlidersHorizontal size={15} />}
        label="More options"
        submenu={(c2) => (
          <Menu style={{ width: 220 }}>
            <MenuItem icon={<Folder size={15} />} label="Move to section" submenu={(c3) => <MoveToSectionMenu channelId={channel.id} close={() => (c3(), c2(), close())} />} />
            <MenuItem icon={<MailOpen size={15} />} label="Mark as read" onClick={() => (c2(), close(), markChannelRead(channel.id))} />
            <MenuItem
              icon={<Mail size={15} />}
              label="Mark as unread"
              onClick={() => {
                c2();
                close();
                if (!markChannelUnread(channel.id)) toast('Nothing to mark unread yet');
              }}
            />
          </Menu>
        )}
      />
      {!isDm && channel.memberIds.includes(me) && (
        <>
          <MenuSeparator />
          <MenuItem
            icon={<LogOut size={15} />}
            label="Leave channel"
            danger
            onClick={() => {
              close();
              leaveChannel(channel);
              toast(`You left #${channel.name}`);
            }}
          />
        </>
      )}
    </Menu>
  );
}

const LIMITS: (number | null)[] = [null, 5, 10, 20];
const limitLabel = (n: number | null | undefined) => (n ? `${n} most recent` : 'No limit');

/** Show (all / unreads), sort (A–Z / recent activity) and limit, shared by every sidebar heading's ⋯ menu. */
function ListPrefsItems({ listKey, close }: { listKey: string; close: () => void }) {
  const prefs = useUi((s) => s.chatSidebar[listKey]) ?? {};
  const set = (changes: ChatListPrefs) => (setChatListPrefs(listKey, changes), close());
  return (
    <>
      <MenuItem
        icon={<ArrowDownUp size={15} />}
        label="Show and sort"
        submenu={(c2) => (
          <Menu style={{ width: 220 }}>
            <MenuLabel>Show</MenuLabel>
            <MenuItem label="All conversations" checked={(prefs.show ?? 'all') === 'all'} onClick={() => (c2(), set({ show: 'all' }))} />
            <MenuItem label="Unreads only" checked={prefs.show === 'unread'} onClick={() => (c2(), set({ show: 'unread' }))} />
            <MenuSeparator />
            <MenuLabel>Sort</MenuLabel>
            <MenuItem label="Alphabetically" checked={(prefs.sort ?? 'alpha') === 'alpha'} onClick={() => (c2(), set({ sort: 'alpha' }))} />
            <MenuItem label="Most recent activity" checked={prefs.sort === 'recent'} onClick={() => (c2(), set({ sort: 'recent' }))} />
          </Menu>
        )}
      />
      <MenuItem
        icon={<Hash size={15} />}
        label="Limit"
        hint={limitLabel(prefs.limit)}
        submenu={(c2) => (
          <Menu style={{ width: 200 }}>
            {LIMITS.map((n) => (
              <MenuItem key={String(n)} label={limitLabel(n)} checked={(prefs.limit ?? null) === n} onClick={() => (c2(), set({ limit: n }))} />
            ))}
          </Menu>
        )}
      />
    </>
  );
}

/** Move my sections up and down. */
function ReorderSections({ workspaceId }: { workspaceId: string }) {
  const me = useMe();
  const sections = useDbShallow((d) => chatSectionsOf(d, me, workspaceId));
  return (
    <div className="menu sb-reorder" style={{ width: 240 }}>
      <div className="menu-label">Reorder sections</div>
      {sections.map((s, i) => (
        <div key={s.id} className="sb-reorder-row">
          <Folder size={15} className="faint" />
          <span className="truncate grow">{s.name}</span>
          <button type="button" className="icon-btn xs" disabled={i === 0} onClick={() => moveChatSection(s.id, -1)} aria-label={`Move ${s.name} up`}>
            <ArrowUp size={13} />
          </button>
          <button type="button" className="icon-btn xs" disabled={i === sections.length - 1} onClick={() => moveChatSection(s.id, 1)} aria-label={`Move ${s.name} down`}>
            <ArrowDown size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

/** The ⋯ on the Channels / Direct Messages headings. */
function HeadingMenu({ kind, channelIds, close }: { kind: 'channels' | 'dms'; channelIds: string[]; close: () => void }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const sectionCount = useDb((d) => chatSectionsOf(d, me, ws).length);
  return (
    <Menu style={{ width: 240 }}>
      {kind === 'channels' ? (
        <MenuItem icon={<Grid3x3 size={15} />} label="Browse channels" onClick={() => (close(), navigate(`/${ws}/chat`))} />
      ) : (
        <MenuItem icon={<Plus size={15} />} label="New message" onClick={() => (close(), navigate(`/${ws}/chat?new=dm`))} />
      )}
      <MenuItem
        icon={<CheckCheck size={15} />}
        label="Mark all as read"
        onClick={() => {
          close();
          for (const id of channelIds) markChannelRead(id);
        }}
      />
      <MenuSeparator />
      <ListPrefsItems listKey={kind} close={close} />
      <MenuSeparator />
      <MenuItem icon={<Plus size={15} />} label="Create section" onClick={() => (close(), openDialog({ kind: 'chat-section', workspaceId: ws }))} />
      <MenuItem icon={<GripVertical size={15} />} label="Reorder sections" disabled={sectionCount < 2} submenu={sectionCount < 2 ? undefined : () => <ReorderSections workspaceId={ws} />} />
    </Menu>
  );
}

export function ChannelSections({ filter }: { filter: 'all' | 'unread' | 'dms' }) {
  const ws = useWorkspaceId();
  const guest = useIsGuest();
  const me = useMe();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const channels = useDb((db) => db.channels);
  const db = useDb((d) => d);
  const unread = JSON.parse(useUnreadByChannel()) as Record<string, number>;
  const expanded = useUi((s) => s.expanded);
  const sections = useDbShallow((d) => chatSectionsOf(d, me, ws));
  const filed = useMemo(() => sectionByChannel(db, me), [db, me]);

  const visible = useMemo(
    () =>
      Object.values(channels)
        .filter((c) => c.workspaceId === ws && (!c.private || c.memberIds.includes(me)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [channels, ws, me],
  );
  // conversations I've muted from their settings (a mute that runs out clears on the next change)
  const muted = new Set(Object.values(db.channelReads).filter((r) => r.userId === me && (r.mutedUntil ?? 0) > Date.now()).map((r) => r.channelId));
  const shown = visible.filter((c) => (filter !== 'unread' || unread[c.id]) && (filter !== 'dms' || c.kind === 'dm'));
  const listPrefs = useUi((s) => s.chatSidebar);
  const messages = useDb((d) => d.messages);
  const lastAt = useMemo(() => {
    const out: Record<string, number> = {};
    for (const m of Object.values(messages)) if (!m.parentId && m.createdAt > (out[m.channelId] ?? 0)) out[m.channelId] = m.createdAt;
    return out;
  }, [messages]);
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  /** A heading's conversations after its show / sort / limit choices, and how many the limit hides. */
  const arrange = (key: string, list: Channel[]): { items: Channel[]; hidden: number } => {
    const p = listPrefs[key] ?? {};
    let out = p.show === 'unread' ? list.filter((c) => unread[c.id]) : list;
    if (p.sort === 'recent') out = [...out].sort((a, b) => (lastAt[b.id] ?? b.createdAt) - (lastAt[a.id] ?? a.createdAt));
    if (!p.limit || showAll[key]) return { items: out, hidden: 0 };
    // over the limit, unread conversations and the open one still show
    const items = out.filter((c, i) => i < p.limit! || unread[c.id] || pathname === `/${ws}/chat/${c.id}`);
    return { items, hidden: out.length - items.length };
  };
  const moreRow = (key: string, hidden: number) =>
    hidden > 0 ? <SidebarItem key="more" icon={<Ellipsis size={16} />} label={`Show ${hidden} more`} muted onClick={() => setShowAll((a) => ({ ...a, [key]: true }))} /> : null;
  const chans = arrange('channels', shown.filter((c) => c.kind === 'channel' && !filed[c.id]));
  const dms = arrange('dms', shown.filter((c) => c.kind === 'dm' && !filed[c.id]));
  const chOpen = expanded['sec:channels'] ?? true;
  const dmOpen = expanded['sec:dms'] ?? true;
  const menu = (c: Channel) => (e: React.MouseEvent) => openContextMenu(e, (close) => <ConversationMenu channel={c} close={close} />);
  // the ⋯ that appears on hover, opening the same menu
  const more = (c: Channel) => (
    <Popover placement="bottom-start" content={(close) => <ConversationMenu channel={c} close={close} />}>
      <button type="button" className="icon-btn xs" aria-label={`Options for ${c.kind === 'dm' ? channelTitle(db, c, me) : '#' + c.name}`}>
        <Ellipsis size={14} />
      </button>
    </Popover>
  );

  const row = (c: Channel) => {
    if (c.kind === 'channel')
      return (
        <SidebarItem
          key={c.id}
          icon={<Hash size={16} />}
          label={
            <span className="row gap-4">
              <span className={`truncate ${unread[c.id] ? 'sb-unread' : ''}`}>{c.name}</span>
              {c.private && <Lock size={11} className="faint" />}
              {muted.has(c.id) && <BellOff size={11} className="faint" />}
            </span>
          }
          badge={unreadBadge(unread[c.id])}
          selected={pathname === `/${ws}/chat/${c.id}`}
          onClick={() => navigate(`/${ws}/chat/${c.id}`)}
          onContextMenu={menu(c)}
          actions={more(c)}
        />
      );
    const other = c.memberIds.find((u) => u !== me) ?? me;
    return (
      <SidebarItem
        key={c.id}
        icon={<Avatar userId={other} size={20} showPresence />}
        label={
          <span className="row gap-4">
            <span className={`truncate ${unread[c.id] ? 'sb-unread' : ''}`}>
              {channelTitle(db, c, me)}
              {other === me && <span className="faint"> — You</span>}
            </span>
            {c.memberIds.length <= 2 && <StatusBadge userId={other} />}
            {muted.has(c.id) && <BellOff size={11} className="faint" />}
          </span>
        }
        badge={unreadBadge(unread[c.id])}
        selected={pathname === `/${ws}/chat/${c.id}`}
        onClick={() => navigate(`/${ws}/chat/${c.id}`)}
        onContextMenu={menu(c)}
        actions={more(c)}
      />
    );
  };

  return (
    <>
      {/* my own sections first, like Slack: each groups the channels and DMs I've moved into it */}
      {sections.map((s, i) => {
        const { items, hidden } = arrange(s.id, shown.filter((c) => filed[c.id] === s.id));
        if (filter !== 'all' && !items.length) return null;
        const open = expanded[`sec:cs:${s.id}`] ?? true;
        return (
          <SidebarSection
            key={s.id}
            title={s.name}
            icon={open ? <FolderOpen size={14} /> : <Folder size={14} />}
            open={open}
            onToggle={() => toggleExpanded(`sec:cs:${s.id}`, !open)}
            actions={
              <Popover
                placement="bottom-end"
                content={(close) => (
                  <Menu style={{ width: 200 }}>
                    <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => (close(), openDialog({ kind: 'rename', c: 'chatSections', id: s.id, name: s.name }))} />
                    <MenuItem icon={<ArrowUp size={15} />} label="Move up" disabled={i === 0} onClick={() => (close(), moveChatSection(s.id, -1))} />
                    <MenuItem icon={<ArrowDown size={15} />} label="Move down" disabled={i === sections.length - 1} onClick={() => (close(), moveChatSection(s.id, 1))} />
                    <MenuSeparator />
                    <ListPrefsItems listKey={s.id} close={close} />
                    <MenuSeparator />
                    <MenuItem
                      icon={<Trash2 size={15} />}
                      label="Delete section"
                      danger
                      onClick={() => {
                        close();
                        deleteChatSection(s.id);
                        toast(`Deleted ${s.name}. Its conversations are back in Channels and Direct Messages.`);
                      }}
                    />
                  </Menu>
                )}
              >
                <button type="button" className="icon-btn xs" aria-label={`${s.name} options`}>
                  <Ellipsis size={14} />
                </button>
              </Popover>
            }
          >
            {items.map(row)}
            {moreRow(s.id, hidden)}
            {!items.length && !hidden && <div className="sb-section-empty">Right-click a channel or DM to move it here</div>}
          </SidebarSection>
        );
      })}
      {filter !== 'dms' && (
        <SidebarSection
          title="Channels"
          icon={<Hash size={14} />}
          open={chOpen}
          onToggle={() => toggleExpanded('sec:channels', !chOpen)}
          actions={
            <>
              <Popover placement="bottom-start" content={(close) => <HeadingMenu kind="channels" channelIds={chans.items.map((c) => c.id)} close={close} />}>
                <button type="button" className="icon-btn xs" aria-label="Channel options">
                  <Ellipsis size={14} />
                </button>
              </Popover>
              {!guest && (
                <button type="button" className="icon-btn xs" onClick={() => navigate(`/${ws}/chat?new=channel`)} aria-label="New channel">
                  <Plus size={14} />
                </button>
              )}
            </>
          }
        >
          {chans.items.map(row)}
          {moreRow('channels', chans.hidden)}
          {filter === 'all' && !guest && <SidebarItem icon={<Plus size={16} />} label="Add Channel" muted onClick={() => navigate(`/${ws}/chat?new=channel`)} />}
        </SidebarSection>
      )}
      <SidebarSection
        title="Direct Messages"
        icon={<MessageCircle size={14} />}
        open={dmOpen}
        onToggle={() => toggleExpanded('sec:dms', !dmOpen)}
        actions={
          <>
            <Popover placement="bottom-start" content={(close) => <HeadingMenu kind="dms" channelIds={dms.items.map((c) => c.id)} close={close} />}>
              <button type="button" className="icon-btn xs" aria-label="Direct message options">
                <Ellipsis size={14} />
              </button>
            </Popover>
            <button type="button" className="icon-btn xs" onClick={() => navigate(`/${ws}/chat?new=dm`)} aria-label="New message">
              <Plus size={14} />
            </button>
          </>
        }
      >
        {dms.items.map(row)}
        {moreRow('dms', dms.hidden)}
        {filter === 'all' && <SidebarItem icon={<Plus size={16} />} label="New message" muted onClick={() => navigate(`/${ws}/chat?new=dm`)} />}
      </SidebarSection>
    </>
  );
}
